import type { TweetReply, SuspiciousActivityReport, ContentQualityMetrics } from '@podx/core/types';
import { config } from '@podx/core/config';
import { logger } from '@podx/core/logger';
import { GenericContentDetector } from './GenericContentDetector.js';
import { BotDetector } from './BotDetector.js';

export class SuspiciousActivityMonitor {
  private genericDetector: GenericContentDetector;
  private botDetector: BotDetector;

  constructor() {
    this.genericDetector = new GenericContentDetector();
    this.botDetector = new BotDetector();
  }

  generateReport(replies: TweetReply[]): SuspiciousActivityReport {
    logger.info('Starting suspicious activity analysis', {
      operation: 'suspicious_activity_analysis',
      replyCount: replies.length
    });

    if (!config.analysis.enableBotDetection && !config.analysis.enableReplyAnalysis) {
      logger.info('Bot detection and reply analysis disabled');
      return this.createEmptyReport(replies.length);
    }

    // Analyze content quality for all replies
    const qualityAnalysis = replies.map(reply => ({
      reply,
      quality: config.analysis.enableReplyAnalysis ? 
        this.genericDetector.analyzeContentQuality(reply) : 
        this.createDefaultQuality()
    }));

    // Perform social graph and interaction analysis
    let socialAnalysis = {
      suspiciousNetworks: [],
      coordinatedBehavior: [],
      replyFarms: []
    };

    if (config.analysis.enableCoordinatedDetection && replies.length >= config.botDetection.minRepliesForAnalysis) {
      socialAnalysis = this.botDetector.analyzeInteractionPatterns(replies);
    }

    // Generate summary scores
    const suspiciousReplies = qualityAnalysis.filter(a => 
      a.quality.spamProbability > config.botDetection.spamProbabilityThreshold
    ).length;
    
    const botProbability = replies.length > 0 ? suspiciousReplies / replies.length : 0;
    const networkRisk = this.calculateNetworkRisk(socialAnalysis);

    // Generate patterns analysis
    const patterns = {
      genericReplies: qualityAnalysis
        .filter(a => a.quality.genericityScore > config.botDetection.genericityThreshold)
        .map(a => ({
          username: a.reply.username,
          content: a.reply.text,
          score: a.quality.genericityScore
        }))
        .sort((a, b) => b.score - a.score)
        .slice(0, 20), // Top 20 most generic replies
      
      coordinatedActivity: socialAnalysis.coordinatedBehavior,
      replyFarms: socialAnalysis.replyFarms,
      suspiciousNetworks: socialAnalysis.suspiciousNetworks
    };

    // Generate recommendations
    const recommendations = this.generateRecommendations(qualityAnalysis, socialAnalysis, {
      totalReplies: replies.length,
      suspiciousReplies,
      botProbability,
      networkRisk
    });

    // Flag suspicious accounts
    const flaggedAccounts = this.flagSuspiciousAccounts(qualityAnalysis, socialAnalysis);

    const report: SuspiciousActivityReport = {
      timestamp: new Date(),
      summary: {
        totalReplies: replies.length,
        suspiciousReplies,
        botProbability: Math.round(botProbability * 100) / 100,
        networkRisk: Math.round(networkRisk * 100) / 100
      },
      patterns,
      recommendations,
      flaggedAccounts
    };

    logger.info('Suspicious activity analysis complete', {
      operation: 'suspicious_activity_complete',
      summary: report.summary,
      flaggedAccountsCount: flaggedAccounts.length,
      recommendationsCount: recommendations.length
    });

    return report;
  }

  private createEmptyReport(totalReplies: number): SuspiciousActivityReport {
    return {
      timestamp: new Date(),
      summary: {
        totalReplies,
        suspiciousReplies: 0,
        botProbability: 0,
        networkRisk: 0
      },
      patterns: {
        genericReplies: [],
        coordinatedActivity: [],
        replyFarms: [],
        suspiciousNetworks: []
      },
      recommendations: ['Bot detection and reply analysis are disabled in configuration'],
      flaggedAccounts: []
    };
  }

  private createDefaultQuality(): ContentQualityMetrics {
    return {
      genericityScore: 0,
      spamProbability: 0,
      manipulationScore: 0,
      originalityScore: 1,
      informationDensity: 0.5,
      credibilityMarkers: [],
      speculationLevel: 0,
      educationalValue: 0
    };
  }

  private calculateNetworkRisk(socialAnalysis: any): number {
    let riskScore = 0;

    // Risk from suspicious networks
    if (socialAnalysis.suspiciousNetworks.length > 0) {
      const avgConfidence = socialAnalysis.suspiciousNetworks
        .reduce((sum: number, network: any) => sum + network.confidence, 0) / 
        socialAnalysis.suspiciousNetworks.length;
      riskScore += avgConfidence * 0.4;
    }

    // Risk from coordinated behavior
    if (socialAnalysis.coordinatedBehavior.length > 0) {
      const avgSuspicion = socialAnalysis.coordinatedBehavior
        .reduce((sum: number, behavior: any) => sum + behavior.suspiciousScore, 0) / 
        socialAnalysis.coordinatedBehavior.length;
      riskScore += avgSuspicion * 0.3;
    }

    // Risk from reply farms
    if (socialAnalysis.replyFarms.length > 0) {
      const avgRisk = socialAnalysis.replyFarms
        .reduce((sum: number, farm: any) => sum + farm.riskScore, 0) / 
        socialAnalysis.replyFarms.length;
      riskScore += avgRisk * 0.3;
    }

    return Math.min(1, riskScore);
  }

  private generateRecommendations(
    qualityAnalysis: Array<{reply: TweetReply, quality: ContentQualityMetrics}>,
    socialAnalysis: any,
    summary: {totalReplies: number, suspiciousReplies: number, botProbability: number, networkRisk: number}
  ): string[] {
    const recommendations: string[] = [];
    const { botDetection, analysis } = config;

    // Content quality recommendations
    if (summary.botProbability > 0.3) {
      recommendations.push(
        `High bot activity detected (${Math.round(summary.botProbability * 100)}% of replies suspicious). Consider filtering replies with spam probability > ${botDetection.spamProbabilityThreshold}.`
      );
    }

    const genericReplies = qualityAnalysis.filter(a => 
      a.quality.genericityScore > botDetection.genericityThreshold
    ).length;
    
    if (genericReplies > summary.totalReplies * 0.4) {
      recommendations.push(
        `High volume of generic replies detected (${genericReplies}/${summary.totalReplies}). Consider focusing on replies with higher originality scores.`
      );
    }

    // Social network recommendations
    if (socialAnalysis.suspiciousNetworks.length > 0) {
      recommendations.push(
        `${socialAnalysis.suspiciousNetworks.length} suspicious networks detected. Review flagged accounts for potential bot farms.`
      );
    }

    if (socialAnalysis.coordinatedBehavior.length > 0) {
      const simultaneousActivity = socialAnalysis.coordinatedBehavior.filter((b: any) => 
        b.type === 'simultaneous_replies'
      ).length;
      
      if (simultaneousActivity > 0) {
        recommendations.push(
          `Coordinated posting activity detected. ${simultaneousActivity} instances of simultaneous replies found.`
        );
      }

      const templateSpreading = socialAnalysis.coordinatedBehavior.filter((b: any) => 
        b.type === 'template_spreading'
      ).length;
      
      if (templateSpreading > 0) {
        recommendations.push(
          `Template spreading detected. ${templateSpreading} instances of nearly identical replies from different accounts.`
        );
      }
    }

    if (socialAnalysis.replyFarms.length > 0) {
      const farmAccounts = socialAnalysis.replyFarms.reduce((total: number, farm: any) => 
        total + farm.accounts.length, 0
      );
      
      recommendations.push(
        `${socialAnalysis.replyFarms.length} reply farms detected involving ${farmAccounts} accounts. Consider excluding these accounts from analysis.`
      );
    }

    // Configuration recommendations
    if (!analysis.enableBotDetection) {
      recommendations.push(
        'Bot detection is disabled. Enable with ENABLE_BOT_DETECTION=true for better spam filtering.'
      );
    }

    if (!analysis.enableReplyAnalysis) {
      recommendations.push(
        'Reply analysis is disabled. Enable with ENABLE_REPLY_ANALYSIS=true for content quality metrics.'
      );
    }

    if (!analysis.enableCoordinatedDetection) {
      recommendations.push(
        'Coordinated behavior detection is disabled. Enable with ENABLE_COORDINATED_DETECTION=true for network analysis.'
      );
    }

    // Quality threshold recommendations
    const highQualityReplies = qualityAnalysis.filter(a => 
      this.genericDetector.isHighQuality(a.quality)
    ).length;
    
    if (highQualityReplies < summary.totalReplies * 0.2) {
      recommendations.push(
        `Only ${Math.round((highQualityReplies / summary.totalReplies) * 100)}% of replies meet high quality standards. Consider adjusting quality thresholds or focusing on verified accounts.`
      );
    }

    // No issues found
    if (recommendations.length === 0 && summary.botProbability < 0.1 && summary.networkRisk < 0.1) {
      recommendations.push(
        'Reply quality appears good. Low levels of bot activity and coordinated behavior detected.'
      );
    }

    return recommendations;
  }

  private flagSuspiciousAccounts(
    qualityAnalysis: Array<{reply: TweetReply, quality: ContentQualityMetrics}>,
    socialAnalysis: any
  ): Array<{username: string, riskScore: number, reasons: string[]}> {
    const accountRisks = new Map<string, {score: number, reasons: string[]}>();
    const { botDetection } = config;

    // Analyze individual account quality
    const accountQuality = new Map<string, ContentQualityMetrics[]>();
    for (const analysis of qualityAnalysis) {
      const username = analysis.reply.username;
      if (!accountQuality.has(username)) {
        accountQuality.set(username, []);
      }
      accountQuality.get(username)!.push(analysis.quality);
    }

    // Calculate risk scores based on content quality
    for (const [username, qualities] of accountQuality) {
      const avgGenericityScore = qualities.reduce((sum, q) => sum + q.genericityScore, 0) / qualities.length;
      const avgSpamProbability = qualities.reduce((sum, q) => sum + q.spamProbability, 0) / qualities.length;
      const avgOriginalityScore = qualities.reduce((sum, q) => sum + q.originalityScore, 0) / qualities.length;
      
      let riskScore = 0;
      const reasons: string[] = [];

      if (avgGenericityScore > botDetection.genericityThreshold) {
        riskScore += 0.3;
        reasons.push(`High generic content (${Math.round(avgGenericityScore * 100)}%)`);
      }

      if (avgSpamProbability > botDetection.spamProbabilityThreshold) {
        riskScore += 0.3;
        reasons.push(`High spam probability (${Math.round(avgSpamProbability * 100)}%)`);
      }

      if (avgOriginalityScore < 0.3) {
        riskScore += 0.2;
        reasons.push(`Low originality (${Math.round(avgOriginalityScore * 100)}%)`);
      }

      const highManipulationCount = qualities.filter(q => q.manipulationScore > 0.5).length;
      if (highManipulationCount > qualities.length * 0.3) {
        riskScore += 0.2;
        reasons.push(`Frequent manipulation patterns (${highManipulationCount}/${qualities.length} replies)`);
      }

      if (riskScore > 0 || reasons.length > 0) {
        accountRisks.set(username, { score: riskScore, reasons });
      }
    }

    // Add accounts from suspicious networks
    for (const network of socialAnalysis.suspiciousNetworks) {
      for (const username of network.accounts) {
        const existing = accountRisks.get(username) || { score: 0, reasons: [] };
        existing.score += network.confidence * 0.4;
        existing.reasons.push(`Part of ${network.networkType} network`);
        accountRisks.set(username, existing);
      }
    }

    // Add accounts from coordinated behavior
    for (const behavior of socialAnalysis.coordinatedBehavior) {
      for (const username of behavior.participants) {
        const existing = accountRisks.get(username) || { score: 0, reasons: [] };
        existing.score += behavior.suspiciousScore * 0.3;
        existing.reasons.push(`Involved in ${behavior.type.replace('_', ' ')}`);
        accountRisks.set(username, existing);
      }
    }

    // Add accounts from reply farms
    for (const farm of socialAnalysis.replyFarms) {
      for (const username of farm.accounts) {
        const existing = accountRisks.get(username) || { score: 0, reasons: [] };
        existing.score += farm.riskScore * 0.4;
        existing.reasons.push(`Member of ${farm.farmType.replace('_', ' ')} farm`);
        accountRisks.set(username, existing);
      }
    }

    // Convert to array and filter by minimum risk score
    const flaggedAccounts = Array.from(accountRisks.entries())
      .map(([username, {score, reasons}]) => ({
        username,
        riskScore: Math.min(1, Math.round(score * 100) / 100),
        reasons: [...new Set(reasons)] // Remove duplicates
      }))
      .filter(account => account.riskScore > analysis.minConfidenceThreshold)
      .sort((a, b) => b.riskScore - a.riskScore)
      .slice(0, 50); // Limit to top 50 most suspicious accounts

    logger.debug('Flagged suspicious accounts', {
      operation: 'flag_suspicious_accounts',
      accountCount: flaggedAccounts.length,
      highRiskCount: flaggedAccounts.filter(a => a.riskScore > 0.7).length
    });

    return flaggedAccounts;
  }

  // Utility method to get account statistics
  getAccountStatistics(replies: TweetReply[]): {
    totalAccounts: number;
    avgRepliesPerAccount: number;
    mostActiveAccounts: Array<{username: string, replyCount: number}>;
    accountEngagementDistribution: {low: number, medium: number, high: number};
  } {
    const accountStats = new Map<string, {replyCount: number, totalEngagement: number}>();
    
    for (const reply of replies) {
      const username = reply.username;
      if (!accountStats.has(username)) {
        accountStats.set(username, {replyCount: 0, totalEngagement: 0});
      }
      const stats = accountStats.get(username)!;
      stats.replyCount++;
      stats.totalEngagement += reply.likeCount + reply.retweetCount;
    }

    const accounts = Array.from(accountStats.entries());
    const totalAccounts = accounts.length;
    const avgRepliesPerAccount = replies.length / Math.max(totalAccounts, 1);
    
    const mostActiveAccounts = accounts
      .map(([username, stats]) => ({username, replyCount: stats.replyCount}))
      .sort((a, b) => b.replyCount - a.replyCount)
      .slice(0, 10);

    // Categorize accounts by engagement level
    let lowEngagement = 0, mediumEngagement = 0, highEngagement = 0;
    for (const [, stats] of accounts) {
      const avgEngagement = stats.totalEngagement / Math.max(stats.replyCount, 1);
      if (avgEngagement < 5) lowEngagement++;
      else if (avgEngagement < 20) mediumEngagement++;
      else highEngagement++;
    }

    return {
      totalAccounts,
      avgRepliesPerAccount: Math.round(avgRepliesPerAccount * 100) / 100,
      mostActiveAccounts,
      accountEngagementDistribution: {
        low: lowEngagement,
        medium: mediumEngagement,
        high: highEngagement
      }
    };
  }

  // Utility method to filter high-quality replies
  filterHighQualityReplies(replies: TweetReply[]): TweetReply[] {
    if (!config.analysis.enableReplyAnalysis) {
      return replies;
    }

    const filteredReplies: TweetReply[] = [];
    
    for (const reply of replies) {
      const quality = this.genericDetector.analyzeContentQuality(reply);
      if (this.genericDetector.isHighQuality(quality)) {
        filteredReplies.push(reply);
      }
    }

    logger.info('Filtered replies by quality', {
      operation: 'filter_quality_replies',
      originalCount: replies.length,
      filteredCount: filteredReplies.length,
      qualityRatio: Math.round((filteredReplies.length / Math.max(replies.length, 1)) * 100) / 100
    });

    return filteredReplies;
  }

  // Utility method to exclude flagged accounts
  excludeFlaggedAccounts(replies: TweetReply[]): TweetReply[] {
    const report = this.generateReport(replies);
    const flaggedUsernames = new Set(report.flaggedAccounts.map(account => account.username));
    
    const cleanReplies = replies.filter(reply => !flaggedUsernames.has(reply.username));
    
    logger.info('Excluded flagged accounts', {
      operation: 'exclude_flagged_accounts',
      originalCount: replies.length,
      cleanCount: cleanReplies.length,
      excludedCount: replies.length - cleanReplies.length
    });

    return cleanReplies;
  }
}